import { useField } from "formik";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  DateSelector,
  DateSelectorProps,
  DateSelectValue,
} from "../components/inputs/date-input/DateSelector";
import {
  validateDateSegments,
  formatDateSelectValueAsISODate,
  parseISODateAsDateSelectValue,
} from "../components/inputs/date-input/utils";

interface DateSelectorFieldProps
  extends Omit<
    DateSelectorProps,
    "isInvalid" | "errorMessage" | "onBlur" | "value" | "onChange"
  > {
  name: string;
}

export function DateSelectorFormikField(props: DateSelectorFieldProps) {
  const { name, ...dateSelectorFieldProps } = props;
  const [field, meta, helpers] = useField(name);
  const [date, setDate] = useState<DateSelectValue>();
  const { t } = useTranslation();

  // builds custom error message bypassing Yup & Formik
  // component is composed of two input fields but one Formik field
  const partialDateErrorMessage = validateDateSegments(
    t,
    date,
    props.label,
    props.granularity
  );

  useEffect(() => {
    const newValue = parseISODateAsDateSelectValue(field.value);

    if (newValue) {
      setDate(newValue);
    }
  }, [field.value]);

  useEffect(() => {
    const isoDate = formatDateSelectValueAsISODate(date);

    if (isoDate) {
      helpers.setValue(isoDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  return (
    <DateSelector
      name={name}
      value={date}
      onChange={setDate}
      isDisabled={props.isDisabled}
      isRequired={props.isRequired}
      isInvalid={Boolean(meta.touched && meta.error)}
      errors={partialDateErrorMessage || meta.touched ? meta.error : undefined}
      onBlur={() => {
        helpers.setTouched(true);
      }}
      {...dateSelectorFieldProps}
    />
  );
}
